// =============================================================================
// ScenarioTruthPanel (L-K) — the scenarios management surface. R386 landed the
// API half of scenario staleness (GET /api/scenarios serves retiredStale /
// staleReason / staleDetail; POST :id/retire + :id/reactivate) and registered
// the follow-up this file closes: the FRONT END never rendered any of it. The
// owner's pre-correction Australia-move scenario was being held back from
// scoring with no way to see THAT it was, or WHY, or to bring it back.
//
// The idiom is deliberately BORROWED, not invented: CorpusArchivePanel's
// archive/undo shape — live rows up top with a reversible action, a collapsed
// "Retired (N)" block below, every retired row greyed and recoverable, an
// honest count, nothing ever deleted. A retired scenario is the same KIND of
// thing as an archived card, so it should look like one.
//
// PURE PRESENTATION over served data. Every state derivation and every mapped
// sentence lives in lib/scenarioTruth.ts (unit-tested); every fetch lives in
// lib/useScenarios.ts. This component invents nothing:
//   · the WHY comes from the served staleReason code, mapped to one of two
//     fixed sentences — an unrecognised code renders NO explanation rather
//     than a guessed one, and never a bare code string;
//   · the DETAIL is the server's own staleDetail sentence, verbatim;
//   · "Bring it back" is offered ONLY where POST /reactivate can actually do
//     something (a retire stamp exists to clear). For a scenario held back by
//     DERIVATION there is no stamp, so instead of a button that would quietly
//     do nothing we render the honest route back — declare the move — which is
//     the same path the API route's own comment names. Offered, never pushed.
// =============================================================================

import { useState } from 'react';
import type { ScenarioRecord } from '../lib/api';
import {
  type ScenarioPending,
  canReactivate,
  describeReactivationPath,
  describeRetiredNote,
  describeScenarioActionLabel,
  describeScenarioMeta,
  describeStaleDetail,
  describeStaleHeadline,
  retiredScenarios,
  scenarioRowState,
  servableScenarios,
} from '../lib/scenarioTruth';
import styles from './ScenarioTruthPanel.module.css';

export interface ScenarioTruthPanelProps {
  scenarios: ScenarioRecord[];
  loading: boolean;
  /** The list read's own failure message (raw server message). */
  error: string | null;
  /** A retire/reactivate failure, already in Meira's voice (the hook composes
   *  it via describeScenarioActionError). Surfaced, never swallowed. */
  actionError: string | null;
  /** Which row has an action in flight — exactly one row greys out. */
  pending: ScenarioPending | null;
  onRetire: (id: string) => void;
  onReactivate: (id: string) => void;
  /** Force the retired block open/closed (the style guide drives this). Left
   *  undefined in the app: the block opens on its own when something is
   *  retired, because a held-back scenario is information he needs, not
   *  clutter to hide — and closes to the same collapsed control the corpus
   *  archive uses once he's read it. */
  defaultShowRetired?: boolean;
}

export function ScenarioTruthPanel({
  scenarios,
  loading,
  error,
  actionError,
  pending,
  onRetire,
  onReactivate,
  defaultShowRetired,
}: ScenarioTruthPanelProps) {
  // null = "the user hasn't touched the toggle", so the default below can
  // still respond when the list arrives after first paint.
  const [showRetiredOverride, setShowRetiredOverride] = useState<boolean | null>(null);

  const live = servableScenarios(scenarios);
  const retired = retiredScenarios(scenarios);
  const showRetired = showRetiredOverride ?? defaultShowRetired ?? retired.length > 0;
  const busy = pending !== null;

  return (
    <section className={styles.panel} aria-label="Search scenarios">
      <header className={styles.head}>
        <h3 className={styles.title}>Search scenarios</h3>
        <p className={styles.sub}>
          Each one is a lens I score jobs through — where you'd work, and what you're authorized for
          there. If one stops matching your life, retire it and I'll stop scoring against it.
          Nothing gets deleted.
        </p>
      </header>

      {error && (
        <div className={styles.error} role="alert">
          I couldn't load your scenarios: {error}
        </div>
      )}

      {actionError && (
        <div className={styles.error} role="alert">
          {actionError}
        </div>
      )}

      {loading && <div className={styles.status}>Reading your scenarios…</div>}

      {!loading && !error && scenarios.length === 0 && (
        <div className={styles.status}>You don't have any search scenarios yet.</div>
      )}

      {live.length > 0 && (
        <div className={styles.group}>
          {live.map((s) => {
            const state = scenarioRowState(s, pending);
            const meta = describeScenarioMeta(s);
            return (
              <div key={s.id} className={styles.row}>
                <div className={styles.rowMain}>
                  <span className={styles.rowName}>
                    {s.name}
                    {s.isPrimary && <span className={styles.primaryChip}>primary</span>}
                  </span>
                  {meta && <span className={styles.rowMeta}>{meta}</span>}
                </div>
                <button
                  type="button"
                  className={styles.rowButton}
                  onClick={() => onRetire(s.id)}
                  disabled={busy}
                >
                  {describeScenarioActionLabel(state)}
                </button>
              </div>
            );
          })}
        </div>
      )}

      {retired.length > 0 && (
        <div className={styles.retiredBlock}>
          <button
            type="button"
            className={styles.retiredToggle}
            onClick={() => setShowRetiredOverride(!showRetired)}
            aria-expanded={showRetired}
          >
            {showRetired ? '▾' : '▸'} Retired ({retired.length})
          </button>

          {showRetired && (
            <div className={styles.retiredList}>
              {retired.map((s) => {
                const state = scenarioRowState(s, pending);
                const meta = describeScenarioMeta(s);
                const why = describeStaleHeadline(s);
                const detail = describeStaleDetail(s);
                const note = describeRetiredNote(s);
                const path = describeReactivationPath(s);
                return (
                  <div key={s.id} className={styles.retiredRow}>
                    <div className={styles.rowMain}>
                      <span className={styles.retiredName}>{s.name}</span>
                      {meta && <span className={styles.rowMeta}>{meta}</span>}
                      {why && <span className={styles.staleWhy}>{why}</span>}
                      {detail && <span className={styles.staleDetail}>{detail}</span>}
                      {note && <span className={styles.staleNote}>Your note: {note}</span>}
                      {path && <span className={styles.reactivationPath}>{path}</span>}
                    </div>
                    {canReactivate(s) && (
                      <button
                        type="button"
                        className={styles.rowButton}
                        onClick={() => onReactivate(s.id)}
                        disabled={busy}
                      >
                        {describeScenarioActionLabel(state)}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </section>
  );
}
